import {
  ActionSubmit,
  InfoButton,
  Input,
  Label,
  Form,
  ValidationContainer,
  ValidationMessage,
  useToasterContext,
} from "@tandemdiabetes/sugarmate-react-components"
import { useNavigate } from "@tanstack/react-router"
import type {
  ChangeEvent,
  FormEventHandler,
  MouseEventHandler,
} from "react"
import {
  useCallback,
  useState,
} from "react"
import { SendInvitationDialog } from "./SendInvitationDialog"
import styles from "./Sharing.module.scss"
import { PageTemplate } from "src/components"
import {
  useOverlayLoader,
  usePolyglot,
} from "src/contexts"
import { useCreateFollowInvite } from "src/hooks"
import {
  InviteFollowerErrorToast,
  InviteFollowerSuccessToast,
} from "src/toasts"
import {
  getSugarmateError,
  injectProps,
} from "src/utils"

const emailId = "follow-invite-email-input"
const emailErrorId = "user-email-input-error"
const polyglotPrefix = "pages.data_sharing.invite_a_follower."

export function InviteFollower() {
  const polyglot = usePolyglot()
  const navigate = useNavigate()

  const { showToast } = useToasterContext()
  const { setIsOverlayLoaderVisible } = useOverlayLoader()
  const inviteFollow = useCreateFollowInvite()

  const [
    email,
    setEmail,
  ] = useState<string>("")

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setEmail(e.target.value)
    },
    [setEmail],
  )

  const [
    inviteFollowerDialogOpen,
    setInviteFollowerDialogOpen,
  ] =
    useState<boolean>(false)

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (ev) => {
      ev.preventDefault()
      ev.stopPropagation()

      setInviteFollowerDialogOpen(true)
    },
    [setInviteFollowerDialogOpen],
  )

  const inviteFollower: MouseEventHandler<HTMLButtonElement> =
    useCallback(
      () => {
        void (async () => {
          setIsOverlayLoaderVisible(true)

          try {
            if (!inviteFollow) {
              throw new Error("Unable to invite follower")
            }

            await inviteFollow({ email: email })

            const SuccessToastComponent = injectProps(
              InviteFollowerSuccessToast,
              { email: email },
            )

            showToast(
              "invite-follower-success-toast",
              SuccessToastComponent,
            )

            await navigate({ to: "/data_sharing/sharing" })
          } catch (error) {
            const sugarmateError = getSugarmateError(error)
            const ErrorToastComponent = injectProps(
              InviteFollowerErrorToast,
              {
                code: sugarmateError?.code,
                status: sugarmateError?.status,
              },
            )
            showToast(
              "invite-follower-error-toast",
              ErrorToastComponent,
            )
          }

          setIsOverlayLoaderVisible(false)
        })()
      },
      [
        inviteFollow,
        email,
        showToast,
        navigate,
        setIsOverlayLoaderVisible,
      ],
    )

  return (
    <PageTemplate
      user="account"
      title={polyglot.t("components.page_header.pages.invite")}
      backRoutePath="/data_sharing/sharing"
    >
      <Form
        className={styles.container}
        onSubmit={onSubmit}
      >
        <div className={styles.description}>
          {polyglot.t(`${polyglotPrefix}description`)}
        </div>
        <div className={styles.actionContainer}>
          <div className={styles.inviteEmailContainer}>
            <Label>
              {polyglot.t(`${polyglotPrefix}email_address.label`)}
              <InfoButton
                title={polyglot.t(`${polyglotPrefix}email_address.info_title`)}
              >
                {polyglot.t(`${polyglotPrefix}email_address.info_description`)}
              </InfoButton>
            </Label>
            <ValidationContainer>
              <Input
                className={styles.emailInput}
                value={email}
                onChange={onChange}
                autoComplete="email"
                id={emailId}
                placeholder={polyglot.t(
                  `${polyglotPrefix}email_address.placeholder`,
                )}
                type="email"
                required
              />
              <ValidationMessage
                id={emailErrorId}
                invalidMessage={{
                  valueMissing: polyglot.t("validation_message.value_missing"),
                  typeMismatch: polyglot.t(
                    "validation_message.email_type_mismatch",
                  ),
                }}
              />
            </ValidationContainer>
          </div>
          <ActionSubmit
            id="invite-btn"
            className={styles.minWidthButton}
            stylePreset="primary"
            value={polyglot.t(`${polyglotPrefix}invite_button.label`)}
          />
        </div>
        <SendInvitationDialog
          open={inviteFollowerDialogOpen}
          setOpen={setInviteFollowerDialogOpen}
          inviteFollower={inviteFollower}
          email={email}
        />
      </Form>
    </PageTemplate>
  )
}
